import { useEffect, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, BRAND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  X, Send, ArrowRight, Sparkles, Wrench, AlertTriangle, Gauge,
  Package, Building2, Boxes, Truck, Headset,
} from "lucide-react";

const QUICK = [
  { id: "setup", icon: Wrench },
  { id: "carbonation", icon: Sparkles },
  { id: "problem", icon: AlertTriangle },
  { id: "co2", icon: Gauge },
  { id: "go", icon: Package },
  { id: "pro", icon: Building2 },
  { id: "accessories", icon: Boxes },
  { id: "orders", icon: Truck },
  { id: "human", icon: Headset },
];

const greetingKey = (pathname) => {
  if (pathname === "/support") return "greeting_support";
  if (pathname.startsWith("/professional")) return "greeting_pro";
  if (pathname === "/preshhgo" || pathname.startsWith("/product")) return "greeting_go";
  return null;
};

// Lightweight, safe markdown for Mat replies: **bold**, *italic*, and bullet lines.
const renderInline = (text, kp) => {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*\n]+\*)/g;
  let last = 0, m, idx = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={`${kp}-b${idx}`}>{tok.slice(2, -2)}</strong>);
    else parts.push(<em key={`${kp}-i${idx}`}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length; idx++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const RichText = ({ text }) => {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      blocks.push(<ul key={`ul${key}`} className="list-disc pl-4 space-y-1 my-1.5">{list}</ul>);
      list = [];
    }
  };
  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (/^([-•*])\s+/.test(trimmed)) {
      list.push(<li key={`li${i}`}>{renderInline(trimmed.replace(/^([-•*])\s+/, ""), `l${i}`)}</li>);
    } else {
      flush(i);
      if (trimmed === "") blocks.push(<div key={`sp${i}`} className="h-1.5" />);
      else blocks.push(<p key={`p${i}`}>{renderInline(line, `p${i}`)}</p>);
    }
  });
  flush("end");
  return <div className="space-y-1">{blocks}</div>;
};

const MatAvatar = ({ size = 40, ring = true }) => (  <img
    src={BRAND.matAvatar}
    alt="Mat"
    style={{ width: size, height: size }}
    className={`rounded-full object-cover ${ring ? "ring-2 ring-[#00B5D8]/40" : ""}`}
  />
);

export function ConciergeWidget() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("preshh_concierge_sid") || null);
  const [busy, setBusy] = useState(false);
  const [cookieDismissed, setCookieDismissed] = useState(() => !!localStorage.getItem("preshh_cookie_consent"));
  const { token, customer } = useAuth();
  const listRef = useRef(null);

  // Lift the launcher once the cookie banner disappears
  useEffect(() => {
    if (cookieDismissed) return;
    const iv = setInterval(() => {
      if (localStorage.getItem("preshh_cookie_consent")) { setCookieDismissed(true); clearInterval(iv); }
    }, 500);
    return () => clearInterval(iv);
  }, [cookieDismissed]);

  // External open trigger (Support page CTAs)
  useEffect(() => {
    const handler = () => { setOpen(true); setNudge(false); };
    window.addEventListener("preshh:open-mat", handler);
    return () => window.removeEventListener("preshh:open-mat", handler);
  }, []);

  // Subtle contextual hint (e.g. after opening the manual on a product page)
  useEffect(() => {
    const hint = () => {
      if (localStorage.getItem("preshh_mat_nudge_dismissed")) return;
      setNudge((n) => n || true);
      sessionStorage.setItem("preshh_mat_nudge_shown", "1");
    };
    window.addEventListener("preshh:mat-hint", hint);
    return () => window.removeEventListener("preshh:mat-hint", hint);
  }, []);

  // Context-aware onboarding message
  useEffect(() => {
    if (open && messages.length === 0) {
      const gk = greetingKey(location.pathname);
      let text;
      if (gk) text = t(`concierge.${gk}`);
      else if (customer) text = t("concierge.greeting_named", { name: (customer.name || "").split(" ")[0] });
      else text = t("concierge.greeting_default");
      setMessages([{ role: "assistant", text }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (sessionId) localStorage.setItem("preshh_concierge_sid", sessionId); }, [sessionId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Non-intrusive nudge: once per session, dismissal stored locally
  useEffect(() => {
    if (open) { setNudge(false); return; }
    if (localStorage.getItem("preshh_mat_nudge_dismissed")) return;
    if (sessionStorage.getItem("preshh_mat_nudge_shown")) return;
    if (location.pathname.startsWith("/admin")) return;
    const onSupport = location.pathname === "/support";
    const delay = onSupport ? 1400 : 25000;
    const id = setTimeout(() => {
      if (!localStorage.getItem("preshh_mat_nudge_dismissed") && !sessionStorage.getItem("preshh_mat_nudge_shown")) {
        setNudge(true);
        sessionStorage.setItem("preshh_mat_nudge_shown", "1");
      }
    }, delay);
    return () => clearTimeout(id);
  }, [location.pathname, open]);

  // Hide entirely on admin routes (after hooks)
  if (location.pathname.startsWith("/admin")) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await api.post(
        "/concierge/chat",
        { message: msg, session_id: sessionId, page: location.pathname },
        { headers }
      );
      if (data.session_id) setSessionId(data.session_id);
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: data.reply,
        internal_links: data.internal_links || [],
        suggested_actions: data.suggested_actions || [],
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", text: t("concierge.error") }]);
    } finally { setBusy(false); }
  };

  const dismissNudge = () => { setNudge(false); localStorage.setItem("preshh_mat_nudge_dismissed", "1"); };
  const openFromNudge = () => { setNudge(false); setOpen(true); };
  const goLink = (url) => { setOpen(false); navigate(url); };

  const showQuick = messages.length <= 1 && !busy;

  return (
    <>
      {/* Nudge */}
      {nudge && !open && (
        <div
          data-testid="mat-nudge"
          className={`fixed right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-[340px] rounded-2xl border border-[#00B5D8]/25 bg-[#0c0c0c]/95 backdrop-blur-xl shadow-2xl p-4 mat-nudge-in ${cookieDismissed ? "bottom-24 sm:bottom-28" : "bottom-56 sm:bottom-56"}`}
        >
          <div className="flex items-start gap-3">
            <MatAvatar size={40} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{t("concierge.nudge.title")}</p>
              <p className="mt-1 text-[13px] leading-snug text-white/60">{t("concierge.nudge.body")}</p>
            </div>
            <button onClick={dismissNudge} data-testid="mat-nudge-close" className="p-1 text-white/40 hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={openFromNudge} data-testid="mat-nudge-cta"
              className="flex-1 bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold text-[13px] rounded-full px-4 py-2 transition-colors">
              {t("concierge.nudge.cta")}
            </button>
            <button onClick={dismissNudge} data-testid="mat-nudge-dismiss"
              className="text-[13px] text-white/50 hover:text-white px-3 py-2 transition-colors">
              {t("concierge.nudge.dismiss")}
            </button>
          </div>
        </div>
      )}

      {/* Launcher */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setNudge(false); }}
          data-testid="concierge-open"
          className={`fixed right-4 sm:right-6 z-50 flex items-center gap-2.5 bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold pl-2 pr-5 py-2 rounded-full shadow-2xl mat-button-in transition-colors ${cookieDismissed ? "bottom-6" : "bottom-40 sm:bottom-44"}`}
          aria-label={t("concierge.open")}
        >
          <span className="relative">
            <MatAvatar size={34} ring={false} />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#22c55e] ring-2 ring-[#00B5D8]" />
          </span>
          <span className="text-sm">{t("concierge.open")}</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed z-[70] bg-[#0a0a0a] flex flex-col overflow-hidden mat-panel-in inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[404px] sm:h-[640px] sm:max-h-[calc(100vh-3rem)] sm:rounded-3xl sm:border sm:border-white/10 sm:shadow-2xl"
        >
          {/* Header */}
          <header className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-white/10 bg-gradient-to-b from-[#0e1618] to-[#0a0a0a]">
            <div className="flex items-center gap-3">
              <div className="relative">
                <MatAvatar size={42} />
                <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#22c55e] ring-2 ring-[#0a0a0a]" />
              </div>
              <div>
                <p className="font-display text-base leading-none">{t("concierge.name")}</p>
                <p className="mt-1 text-[11px] text-white/50 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                  {t("concierge.role")} · {t("concierge.online")}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="concierge-close"
              className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors">
              <X size={20} />
            </button>
          </header>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto mat-scroll px-4 py-5 space-y-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={`concierge-msg-${m.role}-${i}`}
                className={`flex mat-msg-in ${m.role === "user" ? "justify-end" : "justify-start gap-2.5"}`}>
                {m.role === "assistant" && (
                  <div className="flex-shrink-0 mt-0.5"><MatAvatar size={28} ring={false} /></div>
                )}
                <div className={`max-w-[84%] ${m.role === "user" ? "" : "flex-1"}`}>
                  <div className={`px-4 py-3 text-[15px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#00B5D8] text-black rounded-2xl rounded-br-md"
                      : "bg-white/[0.04] border border-white/10 text-white/90 rounded-2xl rounded-tl-md"
                  }`}>
                    {m.role === "assistant"
                      ? <RichText text={m.text} />
                      : <p className="whitespace-pre-wrap">{m.text}</p>}
                    {m.internal_links?.length > 0 && (
                      <div className="mt-3 flex flex-col gap-1.5">
                        {m.internal_links.map((l, k) => (
                          <button key={k} data-testid={`concierge-link-${i}-${k}`} onClick={() => goLink(l.url)}
                            className="group flex items-center justify-between gap-2 text-[13px] bg-black/40 hover:bg-black/60 border border-white/10 hover:border-[#00B5D8]/50 rounded-xl px-3 py-2.5 transition-colors text-left">
                            <span className="text-white/90">{l.label}</span>
                            <ArrowRight size={14} className="text-[#00B5D8] group-hover:translate-x-0.5 transition-transform" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {m.role === "assistant" && m.suggested_actions?.length > 0 && (
                    <div className="mt-2.5">
                      <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5 px-1">{t("concierge.suggested_label")}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {m.suggested_actions.map((sa, k) => (
                          <button key={k} data-testid={`concierge-action-${i}-${k}`} onClick={() => send(sa)}
                            className="text-[12px] bg-[#00B5D8]/10 hover:bg-[#00B5D8]/20 text-[#7fe3f5] border border-[#00B5D8]/25 rounded-full px-3 py-1.5 transition-colors">
                            {sa}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start gap-2.5">
                <div className="flex-shrink-0 mt-0.5"><MatAvatar size={28} ring={false} /></div>
                <div className="px-4 py-3.5 rounded-2xl rounded-tl-md bg-white/[0.04] border border-white/10">
                  <span className="mat-typing inline-flex items-center gap-1.5"><span /><span /><span /></span>
                </div>
              </div>
            )}

            {/* Onboarding quick actions */}
            {showQuick && (
              <div className="pt-1">
                <p className="text-sm font-semibold text-white/80 mb-3 px-1">{t("concierge.prompt")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK.map(({ id, icon: Icon }) => (
                    <button key={id} data-testid={`concierge-quick-${id}`} onClick={() => send(t(`concierge.quick.${id}.starter`))}
                      className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#00B5D8]/40 hover:bg-white/[0.06] transition-all text-left">
                      <Icon size={16} strokeWidth={1.7} className="text-[#00B5D8] flex-shrink-0" />
                      <span className="text-[12.5px] leading-tight text-white/85">{t(`concierge.quick.${id}.label`)}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-center text-[11px] text-white/45">{t("concierge.footer_note")}</p>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/10 p-3 flex items-center gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-[#0a0a0a]">
            <input
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("concierge.input_placeholder")}
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-[15px] focus:outline-none focus:border-[#00B5D8] transition-colors"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="concierge-send"
              aria-label={t("concierge.send")}
              className="w-11 h-11 flex-shrink-0 rounded-full bg-[#00B5D8] hover:bg-[#009AB8] text-black disabled:opacity-40 flex items-center justify-center transition-colors">
              <Send size={17} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
